/**
 * Helpers for recovering from webpack `ChunkLoadError`.
 *
 * Next.js code-splits routes into lazily-loaded chunks. When a chunk fetch fails
 * (the network dropped, or a deploy replaced the chunk hashes), the dynamic
 * import throws `ChunkLoadError` and trips an error boundary.
 *
 * Resetting the React error boundary does NOT fix this: webpack caches the
 * rejected chunk promise, so re-rendering just re-throws the same error. The
 * only reliable recovery is a full page reload, which re-fetches the current
 * chunk manifest.
 */

export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as { name?: string; message?: string }
  return (
    e.name === 'ChunkLoadError' ||
    /Loading chunk \S+ failed|ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(
      e.message ?? '',
    )
  )
}

const RELOAD_TS_KEY = 'chunkReloadAt'
// Throttle window: a chunk that's genuinely gone would otherwise reload in a
// tight loop. After one reload we won't auto-reload again for this long, so the
// user lands on the error screen with a working "Try again" instead.
const RELOAD_THROTTLE_MS = 10000

/**
 * Reload the page to recover from a chunk error, but only once we're online and
 * not more than once per throttle window. Returns a cleanup function that
 * removes the pending online listener (no-op if it reloaded synchronously).
 */
export function recoverFromChunkError(): () => void {
  if (typeof window === 'undefined') return () => {}

  const reload = () => {
    try {
      const last = Number(window.sessionStorage.getItem(RELOAD_TS_KEY) || 0)
      if (Date.now() - last < RELOAD_THROTTLE_MS) return // reloaded too recently
      window.sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()))
    } catch {
      // sessionStorage can throw in private mode; reload anyway.
    }
    window.location.reload()
  }

  if (navigator.onLine) {
    reload()
    return () => {}
  }

  window.addEventListener('online', reload, { once: true })
  return () => window.removeEventListener('online', reload)
}
